import React, { useState, useRef, useEffect, useCallback } from "react";
import { Send } from "lucide-react";
import MentionDropdown from "./MentionDropdown";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Store selected mentions as a real Quill inline format instead of plain
// text. This preserves the highlight in the controlled editor HTML and when
// the comment is later edited or rendered.
const Quill = ReactQuill.Quill;
const Inline = Quill.import("blots/inline");

const MENTION_PALETTE = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#ea580c",
  "#0891b2",
  "#16a34a",
  "#ca8a04",
  "#4f46e5",
];

const hexToRgba = (hex, alpha) => {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((character) => character + character)
          .join("")
      : normalized;
  const value = Number.parseInt(expanded, 16);
  if (!Number.isFinite(value) || expanded.length !== 6) {
    return `rgba(37, 99, 235, ${alpha})`;
  }
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${
    value & 255
  }, ${alpha})`;
};

const getMentionColor = (mention) => {
  if (mention.type === "group") {
    return mention.id === "card" ? "#7c3aed" : "#0891b2";
  }
  if (/^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(mention.color || "")) {
    return mention.color;
  }
  const key = String(mention.id || mention.name || "mention");
  const hash = Array.from(key).reduce(
    (total, character) => (total * 31 + character.charCodeAt(0)) >>> 0,
    0
  );
  return MENTION_PALETTE[hash % MENTION_PALETTE.length];
};

class MemberMentionBlot extends Inline {
  static create(value) {
    const node = super.create();
    if (value?.id) node.setAttribute("data-user-id", value.id);
    if (value?.name) node.setAttribute("data-user-name", value.name);
    if (value?.color) {
      node.style.setProperty("--mention-color", value.color);
      node.style.setProperty("--mention-bg", value.background);
      node.style.setProperty("--mention-border", value.border);
      node.style.setProperty("--mention-hover-bg", value.hoverBackground);
    }
    return node;
  }

  static formats(node) {
    return {
      id: node.getAttribute("data-user-id") || "",
      name: node.getAttribute("data-user-name") || "",
      color: node.style.getPropertyValue("--mention-color") || "",
      background: node.style.getPropertyValue("--mention-bg") || "",
      border: node.style.getPropertyValue("--mention-border") || "",
      hoverBackground:
        node.style.getPropertyValue("--mention-hover-bg") || "",
    };
  }
}

MemberMentionBlot.blotName = "memberMention";
MemberMentionBlot.tagName = "span";
MemberMentionBlot.className = "mention";
Quill.register(MemberMentionBlot, true);

const SimpleCommentEditor = ({
  value = "",
  onChange,
  onMentionSelect,
  onSend,
  placeholder = "Add a comment...",
  projectMembers = [],
  currentUser = null,
  cardMembers = [],
  height = null, // optional taller box (e.g. for editing an existing comment)
}) => {
  const quillRef = useRef(null);
  const [editorValue, setEditorValue] = useState(value || "");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);

  // Update editor value when prop value changes
  useEffect(() => {
    if (value !== editorValue) {
      setEditorValue(value || "");
    }
  }, [value]);

  // Handle text changes and detect mentions

  const handleTextChange = useCallback(
    (content) => {
      setEditorValue(content);

      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      const range = quill.getSelection();

      if (!range) {
        if (onChange) onChange(content);
        return;
      }

      const cursorPosition = range.index;

      // Get plain text instead of HTML
      const plainText = quill.getText();

      const beforeCursor = plainText.substring(0, cursorPosition);
      const mentionMatch = beforeCursor.match(/@(\w*)$/);

      if (mentionMatch) {
        const query = mentionMatch[1];

        setMentionQuery(query);
        setMentionStartIndex(cursorPosition - query.length - 1);

        const editorBounds =
          quillRef.current?.editor?.root?.getBoundingClientRect();

        if (editorBounds) {
          const dropdownHeight = 200;
          const dropdownWidth = 240;

          const viewportHeight = window.innerHeight;

          const spaceBelow = viewportHeight - editorBounds.bottom;
          const spaceAbove = editorBounds.top;

          let top;
          let left;

          if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            top = editorBounds.top - dropdownHeight - 5;
          } else {
            top = editorBounds.bottom + 5;
          }

          if (editorBounds.left + dropdownWidth > window.innerWidth) {
            left = window.innerWidth - dropdownWidth - 10;
          } else {
            left = editorBounds.left;
          }

          setMentionPosition({
            top: Math.max(10, top),
            left: Math.max(10, left),
          });
        }

        setShowMentions(true);
      } else {
        setShowMentions(false);
        setMentionQuery("");
        setMentionStartIndex(-1);
      }

      if (onChange) {
        onChange(content); // HTML
      }
    },
    [onChange]
  );

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, false] }], // Header dropdown
      ["bold", "italic", "underline"], // Text formatting
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }], // Lists
      ["link"], // Links
      ["clean"], // Clear formatting
    ],
  };

  // Handle mention selection
  const handleMentionSelect = useCallback(
    (mention) => {
      const quill = quillRef.current?.getEditor();

      if (!quill) return;

      const range = quill.getSelection(true);

      if (!range) return;

      const endPos = range.index;
      const startPos = mentionStartIndex;

      // remove "@query"
      quill.deleteText(startPos, endPos - startPos);

      const mentionText =
        mention.username ||
        `@${mention.name.replace(/\s+/g, "").toLowerCase()}`;
      const mentionColor = getMentionColor(mention);

      // Insert the highlighted mention and then a separate unformatted space
      // so text typed afterwards does not inherit mention styling.
      quill.insertText(
        startPos,
        mentionText,
        {
          memberMention: {
            id: mention.id,
            name: mention.name,
            color: mentionColor,
            background: hexToRgba(mentionColor, 0.14),
            border: hexToRgba(mentionColor, 0.24),
            hoverBackground: hexToRgba(mentionColor, 0.22),
          },
        },
        "user"
      );
      quill.insertText(
        startPos + mentionText.length,
        " ",
        { memberMention: false },
        "user"
      );

      // move cursor after mention
      quill.setSelection(startPos + mentionText.length + 1, 0);
      // A collapsed Quill selection can inherit the inline format at its
      // boundary. Explicitly turn it off so subsequent comment text and new
      // lines remain plain.
      quill.format("memberMention", false, "silent");

      const html = quill.root.innerHTML;

      setEditorValue(html);

      onChange?.(html);
      onMentionSelect?.(mention);

      setShowMentions(false);
      setMentionQuery("");
      setMentionStartIndex(-1);

      quill.focus();
    },
    [mentionStartIndex, onChange, onMentionSelect]
  );

  // Close mentions dropdown
  const closeMentions = useCallback(() => {
    setShowMentions(false);
    setMentionQuery("");
    setMentionStartIndex(-1);
  }, []);

  // Handle clicks outside to close mentions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMentions && !event.target.closest(".mention-dropdown")) {
        closeMentions();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMentions, closeMentions]);

  // Handle send button click
  const handleSend = useCallback(() => {
    if (editorValue.trim() && onSend) {
      onSend(editorValue);
      setEditorValue("");
    }
  }, [editorValue, onSend]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e) => {
      if (showMentions) {
        if (e.key === "Escape") {
          e.preventDefault();
          closeMentions();
        }
      } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        // Ctrl+Enter or Cmd+Enter to send
        e.preventDefault();
        handleSend();
      }
    },
    [showMentions, closeMentions, handleSend]
  );

  return (
    <div className={`relative ${height ? "simple-comment-editor--tall" : ""}`}>
      {height && (
        <style>
          {`
            .simple-comment-editor--tall .ql-container {
              height: ${height};
              overflow-y: auto;
            }
            .simple-comment-editor--tall .ql-editor {
              min-height: ${height};
            }
          `}
        </style>
      )}
      {/* Simple textarea - no overlay */}
      <ReactQuill
        theme="snow"
        ref={quillRef}
        value={editorValue}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        dir="ltr"
        className="w-full text-gray-900 dark:text-slate-100 outline-none relative z-20"
        spellCheck="false"
        autoCorrect="off"
        autoCapitalize="off"
        autoComplete="off"
        modules={modules}
      />

      {showMentions && (
        <div
          className="mention-dropdown fixed z-50"
          style={{
            top: mentionPosition.top,
            left: mentionPosition.left,
          }}
        >
          <MentionDropdown
            isOpen={showMentions}
            position={mentionPosition}
            onClose={closeMentions}
            onSelect={handleMentionSelect}
            projectMembers={projectMembers}
            currentUser={currentUser}
            cardMembers={cardMembers}
            mentionQuery={mentionQuery}
          />
        </div>
      )}
    </div>
  );
};

export default SimpleCommentEditor;
